<template>
    <!--
      常用 link 切換（Menubar 版）：自足元件，可放任一 .vue 內。
      顯示哪組由「prop.group（手動指定）或當前 route name（自動）」決定；
      整組查無或過濾後為空 → 不渲染（v-if）。
      渲染走 PrimeVue Menubar #item slot：站內用 router-link(custom) 走 SPA 導航、外部用 <a target> 開新分頁。
      ★ hover：Menubar 在 Aura 基底下 light/dark 原生 hover 皆正常，無需 :dt 或任何 CSS 覆寫。
        （Lara 基底曾有「dark 模式 bar 底色與 item hover 同為 surface.800」的撞色致 hover 不可見，換 Aura 後已不存在。）
      ★ v-ripple 點擊水波紋：本專案 vite 用 @primevue/auto-import-resolver（PrimeVueResolver）自動註冊 PrimeVue 元件與指令，
        故 v-ripple 直接可用，無需在 main.ts 手動 app.directive('ripple', Ripple)。其開關狀態受 PrimeVue config.ripple 控制，
        與 ThemeStyle 的 Ripple 開關（applyStyling 設 config.ripple）連動，全站一致。
    -->
    <Menubar v-if="menuModel.length" :model="menuModel">
        <template #item="{ item, props }">
            <!-- 站內：router-link custom → 前端路由（@click navigate 不可省，否則整頁重載）。
                 isExactActive 標當前頁：套 font-bold + text-yellow-600（Noir 主色為黑白灰，text-primary 在 dark 近白無對比，故用黃字確保 light/dark 皆醒目）。 -->
            <router-link v-if="item.route" :to="item.route" custom
                         v-slot="{ href, navigate, isExactActive }">
                <a v-ripple
                   :href="href"
                   v-bind="props.action"
                   @click="navigate"
                   :class="{ 'font-bold text-yellow-600': isExactActive }">
                    <span v-if="item.icon" :class="item.icon" />
                    <span class="ml-2">{{ item.label }}</span>
                </a>
            </router-link>
            <!-- 外部：新分頁（rel=noopener 防 tabnabbing） -->
            <a v-else
               v-ripple
               :href="item.url"
               :target="item.target"
               rel="noopener"
               v-bind="props.action">
                <span v-if="item.icon" :class="item.icon" />
                <span class="ml-2">{{ item.label }}</span>
            </a>
        </template>
    </Menubar>
</template>

<style scoped>
    /* 樣式全交給 PrimeVue 原生 theme token（Aura），不寫任何 CSS 覆寫。 */
</style>

<script lang="ts">
    //normal <script>：只執行一次（除非 F5）
    export default {
        name: module$.favLinks,
        inheritAttrs: false
    }
</script>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useRoute } from 'vue-router'
    import { useI18n } from 'vue-i18n'
    import { utilfcty$ } from '../utils/utility'
    import { module$ } from '../enums'

    // ***************型別***************
    // 單筆常用 link（後端 Bootstrap 的 parameters.favLinks 逐筆回）。
    interface FavLink {
        labelKey: string            // i18n key（前端解析 favLinks.{labelKey}）；中英對照置 locales/*/favLinks.json
        external: boolean           // true=外部網址（新分頁）；false=站內路由
        to?: string                 // 站內目標 route name（external=false 時用；值對齊 module$）
        url?: string                // 外部網址（external=true 時用）
        icon?: string               // FontAwesome class，如 'fa-solid fa-comments'（可省）
        roles?: string[]            // 可見角色白名單（交集過濾）；省略 / 空陣列 = 全部可見
    }

    // Menubar model 項目（由 FavLink 映射；站內帶 route、外部帶 url/target）。
    interface MenuModelItem {
        label: string
        icon?: string
        route?: { name: string }
        url?: string
        target?: string
    }

    // ***************工具物件***************
    const util$ = utilfcty$.vueUse();
    const { t } = useI18n();             // namespace 寫死一處：favLinks
    const route = useRoute();            // 全域當前路由（任一位置皆同一份）

    // ***************屬性 / 計算***************
    // 可選 props：
    //   group — 手動指定 Map key；省略則 fallback 當前 route name。
    //   align / block — 保留以相容既有掛載點；Menubar 版的置中需 CSS，依「先看預設效果」暫不套用（目前無視覺作用）。
    const props = withDefaults(defineProps<{
        group?: string
        align?: 'left' | 'center' | 'right'
        block?: boolean
    }>(), {
        align: 'left',
        block: false
    });

    // 查表 key：優先 prop.group，否則當前 route name。
    const groupKey = computed<string | undefined>(
        () => props.group ?? (route.name as string | undefined)
    );

    // 後端整張 Map（Bootstrap 併入 parameters，比照 idleLogout）；無設定 / 舊版 → 空物件。
    const favLinksMap = computed<Record<string, FavLink[]>>(
        () => (util$.pinia$.app.parameters?.favLinks ?? {}) as Record<string, FavLink[]>
    );

    // 當前 group 對應清單（查無 → 空 → 整體不顯示）。
    const groupLinks = computed<FavLink[]>(() => {
        const key = groupKey.value;
        return key ? (favLinksMap.value[key] ?? []) : [];
    });

    // 使用者角色（security.userInfo.roles，string[]）。
    const userRoles = computed<string[]>(() => util$.pinia$.security.userInfo.roles ?? []);

    // 角色交集過濾。
    // ★ 僅 UI 顯示便利、非安全邊界：未登入也能由匿名 Bootstrap 取得整張 Map（含 url），
    //   前端過濾只隱藏「顯示」、擋不住「看見 url 字串」。機敏入口請走獨立的認證機制，勿放此功能。
    const canSee = (link: FavLink): boolean => {
        const roles = link.roles;
        return !roles || roles.length === 0 || roles.some(r => userRoles.value.includes(r));
    };

    const visibleLinks = computed<FavLink[]>(() => groupLinks.value.filter(canSee));

    // 映射成 Menubar model：站內→route(具名)、外部→url+target；label 經 i18n（隨語系響應）。
    const menuModel = computed<MenuModelItem[]>(() =>
        visibleLinks.value.map(link => link.external
            ? { label: t(`favLinks.${link.labelKey}`), icon: link.icon, url: link.url, target: '_blank' }
            : { label: t(`favLinks.${link.labelKey}`), icon: link.icon, route: { name: link.to as string } }
        )
    );
</script>